import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner, { CardSkeleton, PageLoader } from '../components/LoadingSpinner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import {
  FileText, DollarSign, Download, Receipt, TrendingUp, Calendar, X
} from 'lucide-react';
import { calculateDateRange, getDateRangeLabel } from '../utils/dateUtils';

const COLORS = ['#0A5F38', '#C5A57D', '#6B8E23', '#8B7355', '#2F4F2F', '#82ca9d'];
const PAYMENT_COLORS = {
  paid: '#10B981',   // Green
  partial: '#F59E0B', // Amber
  pending: '#EF4444'  // Red
};
const ORDER_COLORS = {
  'Completed (Orders)': '#0A5F38', // Forest Green
  'Pending (Invoices)': '#C5A57D'  // Warm Tan
};


const AdminReports = () => {
  const [reportsData, setReportsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('');
  const [currentDateFilter, setCurrentDateFilter] = useState({ ...calculateDateRange('all_time'), label: 'Overview' });
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    fetchReportsData();
  }, [currentDateFilter]);

  const fetchReportsData = async () => {
    try {
      setLoading(true);

      const response = await axios.get('/admin/dashboard', {
        params: {
          start_date: currentDateFilter.start_date,
          end_date: currentDateFilter.end_date
        }
      });
      const dashboardData = response.data;

      setReportsData({
        daily_sales: dashboardData.daily_sales || [],
        product_sales: dashboardData.top_products || [],
        monthly_sales: dashboardData.monthly_sales || [],
        category_sales: dashboardData.category_sales || [],
        payment_status_breakdown: dashboardData.payment_status_breakdown || [],
        payment_collection_trend: dashboardData.payment_collection_trend || [],
        order_status_distribution: dashboardData.order_status_distribution || [],
        summary: {
          total_sales_today: dashboardData.total_sales_today,
          total_sales_month: dashboardData.total_sales_month,
          total_sales_overall: dashboardData.total_sales_overall,
          total_invoices: dashboardData.total_invoices,
          total_estimates: dashboardData.total_estimates,
          total_closed_orders: dashboardData.total_closed_orders,
          pending_payments_amount: dashboardData.pending_payments_amount,
          pending_payments: dashboardData.pending_payments
        }
      });

      console.log('📊 Reports Data Loaded with Date Filter:', {
        dateRange: currentDateFilter,
        data: dashboardData
      });

    } catch (error) {
      console.error('Failed to load reports data:', error);
      toast.error('Failed to load reports data.');
      setReportsData({
        daily_sales: [],
        product_sales: [],
        monthly_sales: [],
        category_sales: [],
        payment_status_breakdown: [],
        payment_collection_trend: [],
        order_status_distribution: [],
        summary: {}
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    if (value === 'custom') {
      setShowCustomModal(true);
    } else if (value === '') {
      // Empty value = "Select a filter" = Overview Mode
      // We set range to 'all_time' so the Total Sales card gets overall data
      const range = calculateDateRange('all_time');
      setCurrentDateFilter({ ...range, label: 'Overview' });
    } else {
      const range = calculateDateRange(value);
      setCurrentDateFilter(range);
    }
  };

  const handleApplyCustomRange = () => {
    if (!customStartDate || !customEndDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    if (customStartDate > customEndDate) {
      toast.error('Start date must be before end date');
      return;
    }

    setCurrentDateFilter({
      start_date: customStartDate,
      end_date: customEndDate,
      label: `Custom: ${getDateRangeLabel(customStartDate, customEndDate)}`
    });
    setShowCustomModal(false);
    toast.success('Custom date range applied');
  };

  if (loading && !reportsData) {
    return <PageLoader text="Loading reports data..." />;
  }

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-[#0A5F38]">Reports & Analytics</h1>
              <p className="text-gray-600">Analyze your business performance and trends</p>
              <p className="text-sm text-[#0A5F38] mt-1">
                <Calendar className="inline h-4 w-4 mr-1" />
                {currentDateFilter.label}
              </p>
            </div>
            {/* Date Filter */}
            <div className="flex space-x-3">
              <select
                value={dateRange}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-[#C5A57D]"
              >
                <option value="">Select a filter</option>
                <option value="today">Today</option>
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="this_month">This Month</option>
                <option value="all_time">All Time</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
          </div>
        </div>

        {/* Custom Date Range Modal */}
        {showCustomModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Select Custom Date Range</h3>
                <button
                  onClick={() => setShowCustomModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-[#C5A57D]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-[#C5A57D]"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowCustomModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApplyCustomRange}
                    className="px-4 py-2 bg-[#C5A57D] text-white rounded-lg hover:bg-[#B8985E]"
                  >
                    Apply Filter
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ROW 1: Sales KPIs Conditional Rendering */}
        <h2 className="text-lg font-semibold text-[#0A5F38] mb-4">Sales Financials</h2>

        {/* Dynamic Card Layout: 3 Cards for 'Overview', 1 Card for Filters */}
        {dateRange === '' ? (
          // Default Overview Mode: 3 Cards (Today, Month, All Time)
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Today's Sales */}
            <div className="card">
              <div className="flex items-center">
                <div className="flex-1 min-w-0 pr-4">
                  <p className="text-sm font-medium text-gray-600">Today's Sales</p>
                  <p className="text-2xl font-bold text-slate-900 break-words">₹{reportsData?.summary?.total_sales_today?.toLocaleString() || 0}</p>
                </div>
                <div className="flex-shrink-0 p-3 bg-green-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            {/* This Month's Sales */}
            <div className="card">
              <div className="flex items-center">
                <div className="flex-1 min-w-0 pr-4">
                  <p className="text-sm font-medium text-gray-600">This Month's Sales</p>
                  <p className="text-2xl font-bold text-[#0A5F38] break-words">₹{reportsData?.summary?.total_sales_month?.toLocaleString() || 0}</p>
                </div>
                <div className="flex-shrink-0 p-3 bg-[#0A5F38]/10 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-[#0A5F38]" />
                </div>
              </div>
            </div>

            {/* Total Sales (All Time) */}
            <div className="card">
              <div className="flex items-center">
                <div className="flex-1 min-w-0 pr-4">
                  <p className="text-sm font-medium text-gray-600">Total Sales</p>
                  <p className="text-2xl font-bold text-[#0A5F38] break-words">₹{reportsData?.summary?.total_sales_overall?.toLocaleString() || 0}</p>
                </div>
                <div className="flex-shrink-0 p-3 bg-[#0A5F38]/10 rounded-lg">
                  <DollarSign className="h-6 w-6 text-[#0A5F38]" />
                </div>
              </div>
            </div>

            {/* Total Invoices (Always Visible) */}
            <div className="card">
              <div className="flex items-center">
                <div className="flex-1 min-w-0 pr-4">
                  <p className="text-sm font-medium text-gray-600">Total Invoices</p>
                  <p className="text-2xl font-bold text-[#0A5F38] break-words">{reportsData?.summary?.total_invoices || 0}</p>
                </div>
                <div className="flex-shrink-0 p-3 bg-gray-100 rounded-lg">
                  <Receipt className="h-6 w-6 text-gray-600" />
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Filtered Mode: Single Card for Period Sales + Invoices
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Selected Period Sales (Spans 3 columns visually or just takes first slot) */}
            <div className="card col-span-1 md:col-span-3 bg-[#0A5F38]/5 border-[#0A5F38]/10">
              <div className="flex items-center">
                <div className="flex-1 min-w-0 pr-4">
                  <p className="text-sm font-medium text-[#0A5F38]">Sales ({currentDateFilter.label})</p>
                  <div className="flex items-baseline">
                    <p className="text-3xl font-bold text-[#0A5F38] break-words">₹{reportsData?.summary?.total_sales_overall?.toLocaleString() || 0}</p>
                    <span className="ml-2 text-sm text-[#0A5F38]/70">for selected period</span>
                  </div>
                </div>
                <div className="flex-shrink-0 p-4 bg-[#C5A57D]/20 rounded-xl">
                  <Calendar className="h-8 w-8 text-[#0A5F38]" />
                </div>
              </div>
            </div>

            {/* Total Invoices (Filtered) */}
            <div className="card">
              <div className="flex items-center">
                <div className="flex-1 min-w-0 pr-4">
                  <p className="text-sm font-medium text-gray-600">Invoices (Period)</p>
                  <p className="text-2xl font-bold text-[#0A5F38] break-words">{reportsData?.summary?.total_invoices || 0}</p>
                </div>
                <div className="flex-shrink-0 p-3 bg-gray-100 rounded-lg">
                  <Receipt className="h-6 w-6 text-gray-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ROW 2: Operational KPIs (Always Visible) */}
        <h2 className="text-lg font-semibold text-[#0A5F38] mb-4">Operational & Pending</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Estimates */}
          <div className="card">
            <div className="flex items-center">
              <div className="flex-1 min-w-0 pr-4">
                <p className="text-sm font-medium text-gray-600">Total Estimates</p>
                <p className="text-2xl font-bold text-[#0A5F38]">{reportsData?.summary?.total_estimates || 0}</p>
              </div>
              <div className="flex-shrink-0 p-3 bg-[#C5A57D]/20 rounded-lg">
                <FileText className="h-6 w-6 text-[#C5A57D]" />
              </div>
            </div>
          </div>

          {/* Total Closed Orders */}
          <div className="card">
            <div className="flex items-center">
              <div className="flex-1 min-w-0 pr-4">
                <p className="text-sm font-medium text-gray-600">Total Closed Orders</p>
                <p className="text-2xl font-bold text-[#0A5F38]">{reportsData?.summary?.total_closed_orders || 0}</p>
              </div>
              <div className="flex-shrink-0 p-3 bg-[#C5A57D]/20 rounded-lg">
                <Receipt className="h-6 w-6 text-[#C5A57D]" />
              </div>
            </div>
          </div>

          {/* Pending Payments (Amount) */}
          <div className="card">
            <div className="flex items-center">
              <div className="flex-1 min-w-0 pr-4">
                <p className="text-sm font-medium text-gray-600">Pending Payments</p>
                <p className="text-2xl font-bold text-red-600 break-words">₹{reportsData?.summary?.pending_payments_amount?.toLocaleString() || 0}</p>
              </div>
              <div className="flex-shrink-0 p-3 bg-red-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>

          {/* Pending Invoices (Count) */}
          <div className="card">
            <div className="flex items-center">
              <div className="flex-1 min-w-0 pr-4">
                <p className="text-sm font-medium text-gray-600">Pending Invoices</p>
                <p className="text-2xl font-bold text-amber-600">{reportsData?.summary?.pending_payments || 0}</p>
              </div>
              <div className="flex-shrink-0 p-3 bg-amber-100 rounded-lg">
                <FileText className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Payment Status Breakdown Cards */}
        <h2 className="text-lg font-semibold text-[#0A5F38] mb-4">Invoice & Order Status Breakdown</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {['paid', 'partial', 'pending'].map(status => {
            const data = reportsData?.payment_status_breakdown?.find(d => d._id === status) || { count: 0, amount: 0 };
            const colors = {
              paid: 'bg-green-50 text-green-700 border-green-200',
              partial: 'bg-yellow-50 text-yellow-700 border-yellow-200',
              pending: 'bg-red-50 text-red-700 border-red-200'
            };
            const titles = { paid: 'Fully Paid', partial: 'Partially Paid', pending: 'Pending Payment' };

            return (
              <div key={status} className={`card border ${colors[status] || 'bg-white'}`}>
                <h3 className="text-md font-semibold mb-1 capitalize">{titles[status]}</h3>
                <div className="flex justify-between items-end mt-2">
                  <div>
                    <p className="text-2xl font-bold">{data.count}</p>
                    <p className="text-xs opacity-75">Invoices / Orders</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold">₹{data.amount.toLocaleString()}</p>
                    <p className="text-xs opacity-75">Total Value</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* === MANDATORY CHARTS SECTION === */}
        <h2 className="text-xl font-bold text-[#0A5F38] mb-4 mt-8">Business Analytics</h2>
        <div className="grid lg:grid-cols-2 gap-8 mb-8">

          {/* 1. Daily Sales Trend (Line Chart) */}
          <div className="card">
            <h3 className="text-lg font-semibold text-[#0A5F38] mb-4">Daily Sales Trend ({currentDateFilter.label})</h3>
            <div className="h-[300px]">
              {reportsData?.daily_sales?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={reportsData.daily_sales}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis tickFormatter={(value) => `₹${value}`} width={80} tick={{ fill: '#4B5563', fontSize: 12 }} />
                    <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Amount']} />
                    <Legend />
                    <Line type="monotone" dataKey="invoice_sales" name="Invoices" stroke="#0A5F38" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="closed_order_sales" name="Closed Orders" stroke="#C5A57D" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">No Data Available</div>
              )}
            </div>
          </div>

          {/* 2. Nested Payment Graph (Stacked Column Chart) */}
          <div className="card">
            <h3 className="text-lg font-semibold text-[#0A5F38] mb-4">Daily Payment Composition</h3>
            <div className="h-[300px]">
              {reportsData?.payment_collection_trend?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={reportsData.payment_collection_trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis tickFormatter={(value) => `₹${value}`} width={80} tick={{ fill: '#4B5563', fontSize: 12 }} />
                    <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Amount']} />
                    <Legend />
                    <Bar dataKey="fully_paid" name="Fully Paid" stackId="a" fill="#10B981" />
                    <Bar dataKey="partially_paid" name="Partially Paid" stackId="a" fill="#F59E0B" />
                    <Bar dataKey="pending" name="Pending Payments" stackId="a" fill="#EF4444" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">No Data Available</div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminReports;


